import React, { useEffect, useState } from "react"
import { select } from "d3-selection"
import { Button, Dropdown, DropdownButton } from "react-bootstrap"

import TagOptionsWidget from "../../resources/TagOptionsWidget"
import { getAfterDragElementOrder } from "../../resources/utils"
import { isNumber } from "../../../utils"
import { borderStyle, registration, Required } from "./utils"
import { withGraphState } from "../../../config/GraphConfigProvider"

function TagFormElement({
  intl,
  selectedMetrics = [],
  setSelectedMetrics,
  plotList = [],
  siteReactors = [],
  queryTags = [],
}) {
  const [tagDetails, setTagDetails] = useState([])
  useEffect(() => {
    setTagDetails(queryTags)
  }, [])

  useEffect(() => {
    // Make sure we have any tag labels for initially passed metrics
    const modMetrics = Object.assign([], selectedMetrics)
    setSelectedMetrics(
      modMetrics.map((m) => {
        const match = queryTags.find((t) => t.name === m.tag)
        return { label: match ? match.label : "", unit_options: match ? match.units_options : [], ...m }
      }),
    )
  }, [tagDetails])

  const updateTagList = (selections) => {
    // Handle response from selections made to add more metrics to lookup list
    let modMetrics = Object.assign([], selectedMetrics)
    let metricsUpdated = false
    selections.forEach((sel) => {
      const tag = sel?.observations ? sel.observations[0].tag : null
      if (tag && !selectedMetrics.map((t) => t.tag).includes(tag)) {
        metricsUpdated = true
        modMetrics.push({ ...sel.observations[0] })
      }
    })
    metricsUpdated && setSelectedMetrics(modMetrics)
  }

  const updateMetrics = (idx, field, val) => {
    const modMetrics = Object.assign([], selectedMetrics)
    setSelectedMetrics(
      modMetrics.map((m, i) => {
        if (i === idx) {
          return { ...m, [field]: val }
        } else {
          return m
        }
      }),
    )
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div>
          <label style={{ margin: "0" }}>{registration.choose_metrics(intl)}:</label>
          {selectedMetrics.length === 0 && <Required intl={intl} />}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <TagOptionsWidget
            plotList={plotList}
            updateTagList={updateTagList}
            siteReactors={siteReactors}
            intl={intl}
            placement={"left"}
            style={{ float: "right", marginTop: "5px" }}
          />
        </div>
      </div>
      <hr />
      <div style={{ height: "400px", overflowY: "scroll", ...borderStyle }}>
        {selectedMetrics.map((metric, idx) => {
          const { label, tag, units, unit_options = [], min, max } = metric
          const tabLabel = label || tag
          const tagElemID = `${tag}_selector`
          const draggableClass = "tag-draggable"
          const onTagDrag = (ev) =>
            getAfterDragElementOrder(
              draggableClass,
              select(`#${tagElemID}`),
              ev.clientX,
              ev.clientY,
              (newIndex) => {
                if (idx !== newIndex) {
                  const modMetrics = [...selectedMetrics]
                  let tag = modMetrics.splice(idx, 1)[0]
                  modMetrics.splice(newIndex, 0, tag)
                  setSelectedMetrics(modMetrics)
                }
              },
            )
          const nonDraggable = {
            draggable: true,
            onDragStart: (e) => e.preventDefault(),
            onDragEnd: (e) => e.preventDefault(),
          }
          return (
            <div
              className={`row draggable ${draggableClass}`}
              index={idx} // Handle for drag/drop reorder functionality
              draggable={true}
              id={tagElemID}
              onDragEnd={(ev) => onTagDrag(ev)}
              key={`tagEntry${idx}`}
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                padding: "5px 0 9px 0",
                margin: "0",
                cursor: "pointer",
                ...borderStyle,
              }}
            >
              <div className={"row col-7"} style={{ margin: "0", padding: "0 5px 5px 0" }}>
                <div className={"col-1"} style={{ padding: "0 0 0 5px" }}>
                  <span className="fa fa-bars" />
                </div>
                <div className={"col-7"} style={{ display: "block", padding: "0 0 0 5px" }}>
                  <div style={{ fontSize: "80%" }}>
                    <input
                      type={"text"}
                      style={{ width: "100%" }}
                      value={tabLabel}
                      {...nonDraggable}
                      onChange={(e) => updateMetrics(idx, "label", e.target.value)}
                    />
                  </div>
                  <div style={{ fontSize: "75%", fontStyle: "italic" }}>
                    <p style={{ margin: "0" }}>{`[${tag}]`}</p>
                  </div>
                </div>
                <div className={"col-4 text-center"} style={{ display: "block", padding: "0 0 0 5px" }}>
                  <DropdownButton
                    size={"sm"}
                    variant={"outline-primary"}
                    className={"site-option"}
                    {...nonDraggable}
                    title={units}
                  >
                    {unit_options.map((u) => (
                      <Dropdown.Item key={u} eventKey={u} onClick={() => updateMetrics(idx, "units", u)}>
                        {u}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </div>
              </div>
              <div className={"col-2"} style={{ padding: "0 5px 5px 0" }}>
                <label style={{ margin: "0", fontSize: "90%" }}>{registration.min_label(intl)}:</label>
                &nbsp;
                <input
                  style={{ width: "100%", fontSize: "80%" }}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  step="0.01"
                  value={isNumber(min) ? min : ""}
                  {...nonDraggable}
                  onChange={(e) => updateMetrics(idx, "min", e.target.value)}
                />
              </div>
              <div className={"col-2"} style={{ padding: "0 5px 5px 0" }}>
                <label style={{ margin: "0", fontSize: "90%" }}>{registration.max_label(intl)}:</label>
                &nbsp;
                <input
                  style={{ width: "100%", fontSize: "80%" }}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  step="0.01"
                  value={isNumber(max) ? max : ""}
                  {...nonDraggable}
                  onChange={(e) => updateMetrics(idx, "max", e.target.value)}
                />
              </div>
              <div className={"col-1"} style={{ padding: "0 5px 5px 0" }}>
                <Button
                  style={{ float: "right" }}
                  variant={"outline-primary"}
                  className={"btn btn-sm"}
                  disabled={selectedMetrics.length <= 1}
                  {...nonDraggable}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    const modMetrics = [...selectedMetrics]
                    modMetrics.splice(idx, 1)
                    setSelectedMetrics(modMetrics)
                  }}
                >
                  <span className="fa fa-minus-circle" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default withGraphState(TagFormElement)
